/**
 * useRelativeTime Hook
 *
 * Auto-updating relative time display that re-renders when needed.
 * Automatically uses the current i18next language for locale-aware formatting.
 * For recent timestamps (< 1 hour), it updates every minute.
 * For very recent timestamps (< 1 minute), it updates every second.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRelativeTime, getRelativeTimeUpdateInterval, Locale } from '../utils/relativeTime';

interface UseRelativeTimeOptions {
  locale?: Locale;
  showSeconds?: boolean;
  disabled?: boolean;
}

interface UseRelativeTimeReturn {
  relativeTime: string;
  forceUpdate: () => void;
}

/**
 * Hook that returns a relative time string that auto-updates.
 * Uses the current i18next language unless an explicit locale is provided.
 *
 * @param dateInput - The date to compare (createdAt timestamp)
 * @param options - Configuration options
 * @returns Object with relativeTime string and forceUpdate function
 */
export function useRelativeTime(
  dateInput: string | Date | number | undefined | null,
  options: UseRelativeTimeOptions = {}
): UseRelativeTimeReturn {
  const { i18n } = useTranslation();
  // Use explicit locale if provided, otherwise derive from i18next language
  const locale = options.locale || (i18n.language as Locale) || 'es';
  const { showSeconds = false, disabled = false } = options;

  // Calculate the relative time
  const calculateTime = useCallback(() => {
    if (!dateInput || disabled) {
      return '';
    }
    return getRelativeTime(dateInput, { locale, showSeconds });
  }, [dateInput, locale, showSeconds, disabled]);

  const [relativeTime, setRelativeTime] = useState(calculateTime);

  // Determine update interval based on how recent the timestamp is
  const updateInterval = useMemo(() => {
    if (!dateInput || disabled) return 0;
    return getRelativeTimeUpdateInterval(dateInput);
  }, [dateInput, disabled]);

  // Force update function for manual refresh
  const forceUpdate = useCallback(() => {
    setRelativeTime(calculateTime());
  }, [calculateTime]);

  // Set up auto-update interval
  useEffect(() => {
    // Initial calculation
    setRelativeTime(calculateTime());

    // If no update interval needed, don't set up timer
    if (updateInterval === 0) return;

    const timer = setInterval(() => {
      setRelativeTime(calculateTime());
    }, updateInterval);

    return () => clearInterval(timer);
  }, [calculateTime, updateInterval]);

  // Update when dateInput changes
  useEffect(() => {
    setRelativeTime(calculateTime());
  }, [dateInput, calculateTime]);

  return { relativeTime, forceUpdate };
}

/**
 * Batch relative time hook for multiple items.
 * Optimizes updates by using a single timer for all items.
 */
export function useBatchRelativeTime(
  items: Array<{ id: string; createdAt: string | Date | number }>,
  localeOverride?: Locale
): Map<string, string> {
  const { i18n } = useTranslation();
  const locale = localeOverride || (i18n.language as Locale) || 'es';
  const [timeMap, setTimeMap] = useState<Map<string, string>>(new Map());

  // Calculate all relative times
  const calculateAll = useCallback(() => {
    const newMap = new Map<string, string>();
    items.forEach(item => {
      if (item.createdAt) {
        newMap.set(item.id, getRelativeTime(item.createdAt, { locale }));
      }
    });
    return newMap;
  }, [items, locale]);

  // Determine the shortest update interval needed
  const minUpdateInterval = useMemo(() => {
    let minInterval = 0;
    items.forEach(item => {
      if (item.createdAt) {
        const interval = getRelativeTimeUpdateInterval(item.createdAt);
        if (interval > 0 && (minInterval === 0 || interval < minInterval)) {
          minInterval = interval;
        }
      }
    });
    return minInterval;
  }, [items]);

  useEffect(() => {
    setTimeMap(calculateAll());

    if (minUpdateInterval === 0) return;

    const timer = setInterval(() => {
      setTimeMap(calculateAll());
    }, minUpdateInterval);

    return () => clearInterval(timer);
  }, [calculateAll, minUpdateInterval]);

  return timeMap;
}

export type { UseRelativeTimeOptions, UseRelativeTimeReturn };
